import { createApi } from '@reduxjs/toolkit/query/react';
import baseApi from './base';
import paginationParams from 'utils/pagination-params';

export const list = 'LIST_USERS';
export const single = 'SINGLE_USER';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseApi,
  tagTypes: [list, single],
  endpoints: (builder) => ({
    createUser: builder.mutation({
      query: ({ lob: _, linesOfBusiness, ...user }) => ({
        url: '/users/create',
        method: 'POST',
        body: { ...user, ...(linesOfBusiness && { linesOfBusiness: linesOfBusiness.map((lob) => lob.id ?? lob) }) }
      }),
      async onQueryStarted({ linesOfBusiness, lob }, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('listUsers', lob ? { lob } : undefined, (draft) => {
              draft.data.items.unshift({ ...created.data, linesOfBusiness });
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    updateUser: builder.mutation({
      query: ({ userId, lob: _, linesOfBusiness, ...attributes }) => ({
        url: `/users/update/${userId}`,
        method: 'POST',
        body: { ...attributes, ...(linesOfBusiness && { linesOfBusiness: linesOfBusiness.map((lob) => lob.id ?? lob) }) }
      }),
      async onQueryStarted({ userId, linesOfBusiness, lob }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedUser } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('listUsers', lob ? { lob } : undefined, (draft) => {
              const user = draft.data.items.find((user) => user.id === userId);
              if (user) {
                Object.assign(user, { ...updatedUser.data, linesOfBusiness });
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      },
      invalidatesTags: [single]
    }),
    deleteUser: builder.mutation({
      query: ({ userId }) => ({
        url: `/users/delete/${userId}`,
        method: 'DELETE'
      }),
      async onQueryStarted({ userId: id, lob }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('listUsers', lob ? { lob } : undefined, (draft) => {
              const idx = draft.data.items.findIndex((item) => item.id === id);
              if (idx > -1) {
                draft.data.items.splice(idx, 1);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getUserById: builder.query({
      query: (userId) => `/users/get/${userId}`,
      providesTags: [single]
    }),
    listUsers: builder.query({
      query: (params) => ({
        url: '/users/list',
        params: params
      }),
      providesTags: [list],
      ...paginationParams
    }),
    sendPasswordEmail: builder.mutation({
      query: (values) => ({
        url: `/users/send-password-email`,
        method: 'POST',
        body: values
      })
    })
  })
});

export const {
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useListUsersQuery,
  useSendPasswordEmailMutation
} = usersApi;
